/**
 * The visitor's cookie choice, in one place.
 *
 * WHY THIS EXISTS (2026-09-30). Until now the site needed no banner: Vercel Analytics is
 * cookieless and Calendly only loads behind a deliberate press. Getting ready for paid ads adds
 * the Meta Pixel and the Google tag, and both write cookies the moment they start. PECR wants
 * consent BEFORE that, so neither script is fetched at all until the visitor presses Accept.
 * "Reject" means the scripts never load, not that they load and promise to behave.
 *
 * NO VENDOR, NO BANNER. If neither tag id is configured the banner never shows and the site
 * behaves exactly as it did before: cookieless. That keeps production unchanged until the ids
 * are deliberately set there, and it means a preview can carry the ids without touching live.
 *
 * The choice is kept in localStorage, which is itself storage on the device, but it is the
 * strictly necessary kind: it exists only to remember the answer to the consent question.
 */

export type ConsentChoice = "granted" | "denied";

export type StoredConsent = {
  choice: ConsentChoice;
  /** Epoch milliseconds when the choice was made. */
  at: number;
  /** Bumped when what we ask consent FOR changes, so an old yes is not stretched to cover it. */
  version: number;
};

export const CONSENT_STORAGE_KEY = "ownly-cookie-consent";

/** Bump this if the list of tags changes in a way a previous "yes" did not cover. */
export const CONSENT_VERSION = 1;

/**
 * Ask again after a year. The ICO does not set a number; a year is the common reading of
 * "refresh consent at appropriate intervals" and it also lets a "no" lapse politely.
 */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** Fired on window whenever the choice changes, so every listener on the page stays in step. */
export const CONSENT_CHANGE_EVENT = "ownly:consent-change";

/** Fired by the footer's "Cookie settings" button to bring the banner back. */
export const CONSENT_OPEN_EVENT = "ownly:consent-open";

/** Parses what is in storage. Anything malformed, stale or from an older version counts as no answer. */
export function parseStoredConsent(raw: string | null, now: number): StoredConsent | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const { choice, at, version } = value as Partial<StoredConsent>;
  if (choice !== "granted" && choice !== "denied") return null;
  if (typeof at !== "number" || !Number.isFinite(at) || at > now + 60_000) return null;
  if (version !== CONSENT_VERSION) return null;
  if (now - at > CONSENT_MAX_AGE_MS) return null;
  return { choice, at, version };
}

export function readConsent(): ConsentChoice | null {
  try {
    return parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY), Date.now())?.choice ?? null;
  } catch {
    // Storage blocked (some private modes). No stored answer means no tags, which is the safe side.
    return null;
  }
}

export function writeConsent(choice: ConsentChoice): void {
  const stored: StoredConsent = { choice, at: Date.now(), version: CONSENT_VERSION };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // The choice still applies to this page view; it just will not be remembered.
  }
  window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: choice }));
}

export function openConsentSettings(): void {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

/**
 * Cookies the Meta Pixel and the Google tag set on our own domain. Cleared when someone who said
 * yes changes their mind, so withdrawing is as complete as we can make it from this side.
 */
export const AD_COOKIE_PATTERN = /^(?:_fbp|_fbc|_ga(?:_.*)?|_gid|_gcl_.*|_gac_.*)$/;

export function clearAdCookies(): void {
  const names = document.cookie
    .split(";")
    .map((part) => part.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name) && AD_COOKIE_PATTERN.test(name));
  const host = window.location.hostname;
  // The tags set cookies on the registrable domain (".ownlydigital.co.uk"), so try each level.
  const parts = host.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`.${parts.slice(i).join(".")}`);
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}
